import { useEffect, useRef, useState, useLayoutEffect, useCallback } from 'react';
import classnames from 'classnames';

import { throttle } from '@hh.ru/magritte-common-func-utils';
import { FcWithFlag } from '@hh.ru/magritte-types';
import { ActionBarProps } from '@hh.ru/magritte-ui-action-bar/types';
import { getScrollableParent, shouldShowDividerOrProgress } from '@hh.ru/magritte-ui-action-bar/utils';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { STRETCHED_WRAPPER_CLASS } from '@hh.ru/magritte-ui-button';
import { Divider } from '@hh.ru/magritte-ui-divider';
import { Layer } from '@hh.ru/magritte-ui-layer';

import styles from './action-bar.less';

const SCROLL_HANDLER_THROTTLE_MS = 50;

export const ActionBar: FcWithFlag<ActionBarProps, 'isActionBar'> = ({
    type = 'page',
    showDivider,
    showProgress = false,
    primaryActions,
    secondaryActions,
    additionalContent,
    wrapperRef,
}) => {
    const { breakpoint } = useBreakpoint();

    const [isScrollEnd, setIsScrollEnd] = useState(false);
    const actionBarRef = useRef<HTMLDivElement>(null);
    const additionalContentRef = useRef<HTMLDivElement>(null);
    const additionalContentWrapperRef = useRef<HTMLDivElement>(null);
    const actionsStackWrapperRef = useRef<HTMLDivElement>(null);
    const actionsStackRef = useRef<HTMLDivElement>(null);
    const primaryActionsRef = useRef<HTMLDivElement>(null);
    const secondaryActionsRef = useRef<HTMLDivElement>(null);

    const { shouldShowProgress, shouldShowDivider } = shouldShowDividerOrProgress({
        type,
        showDivider: type === 'modal' || type === 'modal-vertical' ? false : showDivider,
        showProgress,
        isScrollEnd,
    });

    useEffect(() => {
        if (shouldShowProgress || typeof showDivider === 'boolean') {
            return void 0;
        }
        const scrollableParent = getScrollableParent(actionBarRef.current);
        const handleScroll = throttle(() => {
            const isScrollEnd =
                scrollableParent &&
                Math.abs(scrollableParent.scrollHeight - scrollableParent.clientHeight - scrollableParent.scrollTop) <
                    1;
            setIsScrollEnd(!!isScrollEnd);
        }, SCROLL_HANDLER_THROTTLE_MS);

        if (scrollableParent === document.documentElement) {
            window.addEventListener('scroll', handleScroll);
        } else if (scrollableParent) {
            scrollableParent.addEventListener('scroll', handleScroll);
        }

        return () => {
            window.removeEventListener('scroll', handleScroll);
            scrollableParent?.removeEventListener('scroll', handleScroll);
        };
    }, [shouldShowProgress, showDivider]);

    useEffect(() => {
        if (wrapperRef && actionBarRef.current) {
            wrapperRef.current = actionBarRef.current.offsetHeight;
        }
    }, [wrapperRef, type, primaryActions, secondaryActions]);

    const toggleVerticalLayout = useCallback((enable: boolean) => {
        actionsStackRef.current?.classList.toggle(styles.actionsStackModalVertical, enable);
        primaryActionsRef.current?.classList.toggle(STRETCHED_WRAPPER_CLASS, enable);
        primaryActionsRef.current?.classList.toggle(styles.verticalActionsContainer, enable);
        primaryActionsRef.current?.classList.toggle(styles.horizontalActionsContainer, !enable);
        secondaryActionsRef.current?.classList.toggle(STRETCHED_WRAPPER_CLASS, enable);
        secondaryActionsRef.current?.classList.toggle(styles.verticalActionsContainer, enable);
        secondaryActionsRef.current?.classList.toggle(styles.horizontalActionsContainer, !enable);
    }, []);

    // Эффект проверяющий уместилось ли все содержимое экшн бара в его контейнер и переключающий отображение на
    // вертикальное если не уместилось. Работает только для модалки. Намеренно не триггерится на ресайз контента,
    // чтобы ориентация определялась только при первичном рендере и при смене брейкпоинтов. Чтобы если например
    // кнопка из-за смены надписи стала на пару десятков пикселей шире интерфейс модалки резко не перепрыгивал
    // в вертикальный вариант.
    useLayoutEffect(() => {
        if (type !== 'modal' || !actionsStackRef.current || !actionsStackWrapperRef.current) {
            return void 0;
        }

        toggleVerticalLayout(false);
        const enableVerticalLayout =
            (!!additionalContentRef.current &&
                !!additionalContentWrapperRef.current &&
                (additionalContentRef.current.offsetHeight > additionalContentWrapperRef.current.offsetHeight ||
                    additionalContentRef.current.offsetWidth > additionalContentWrapperRef.current.offsetWidth)) ||
            actionsStackRef.current.scrollWidth > actionsStackWrapperRef.current.offsetWidth;
        toggleVerticalLayout(enableVerticalLayout);

        return void 0;
    }, [type, primaryActions, secondaryActions, additionalContent, breakpoint, toggleVerticalLayout]);

    if (!primaryActions && !secondaryActions && !additionalContent) {
        return null;
    }

    const dataQa = `actions-container-${type}`;

    return (
        <Layer layer="action-bar">
            <div
                ref={actionBarRef}
                data-qa="action-bar"
                className={classnames({
                    [styles.actionBarFixed]: type === 'page',
                    [styles.actionBarWithProgressBar]: shouldShowProgress,
                })}
            >
                {shouldShowDivider && (
                    <div data-qa="divider">
                        <Divider mode="horizontal" />
                    </div>
                )}
                {shouldShowProgress && (
                    <div data-qa="progress-bar" className={styles.progressBar}>
                        {showProgress}
                    </div>
                )}
                <div data-qa="actions-container" className={styles[`actions-wrapper_${type}`]}>
                    {/* Не получилось заюзать useBreakpoint, т.к. он по умолчанию при первичном рендере возвращает isXS = true
                    Из-за этого для type="page" на >XS экранах сначала рендерится вертикальный стэк хотя должен быть горизонтальный
                    это может быть критично для определения высоты экшен бара */}
                    {(type === 'page' || type === 'mobile') && (
                        <div
                            className={classnames(styles.actionsStack, {
                                [styles.actionsStackVerticalOnMobile]: type === 'mobile',
                                [styles.actionsStackVerticalOnPageXs]: type === 'page',
                            })}
                            data-qa={`${dataQa} actions-container-page-or-mobile`}
                        >
                            {!!additionalContent && (
                                <div
                                    className={classnames(styles.additionalContentContainer, {
                                        [styles.additionalContentMobile]: type === 'mobile',
                                    })}
                                    data-qa="additional-content"
                                >
                                    {additionalContent}
                                </div>
                            )}
                            {!!primaryActions && (
                                <div
                                    className={`${STRETCHED_WRAPPER_CLASS} ${styles.verticalActionsContainer}`}
                                    data-qa="primary-actions"
                                >
                                    {primaryActions}
                                </div>
                            )}
                            {!!secondaryActions && (
                                <div
                                    className={`${STRETCHED_WRAPPER_CLASS} ${styles.verticalActionsContainer}`}
                                    data-qa="secondary-actions"
                                >
                                    {secondaryActions}
                                </div>
                            )}
                        </div>
                    )}
                    {type === 'page' && (
                        <div
                            className={classnames(styles.actionsStack, styles.actionsStackHorizontalOnGtXs, {
                                [styles.rightOnlyActions]: !secondaryActions && !additionalContent,
                            })}
                            data-qa={`${dataQa} actions-container-page-only`}
                        >
                            {!!secondaryActions && (
                                <div className={styles.horizontalActionsContainer} data-qa="secondary-actions">
                                    {secondaryActions}
                                </div>
                            )}
                            {!!additionalContent && (
                                <div className={styles.additionalContentContainer}>
                                    <div
                                        className={classnames(styles.additionalContentWrapper, {
                                            [styles.additionalContentOverflowWrapper]:
                                                !!primaryActions || !!secondaryActions,
                                        })}
                                        data-qa="additional-content"
                                    >
                                        {additionalContent}
                                    </div>
                                </div>
                            )}
                            {!!primaryActions && (
                                <div className={styles.horizontalActionsContainer} data-qa="primary-actions">
                                    {primaryActions}
                                </div>
                            )}
                        </div>
                    )}
                    {(type === 'modal' || type === 'modal-vertical') && (
                        <div ref={actionsStackWrapperRef} className={styles.actionsStackWrapper}>
                            <div
                                className={classnames(styles.actionsStack, styles.actionsStackModal, {
                                    [styles.actionsStackModalVertical]: type === 'modal-vertical',
                                    [styles.rightOnlyActions]: !secondaryActions && !additionalContent,
                                })}
                                ref={actionsStackRef}
                                data-qa={`${dataQa} actions-container-modal-or-vertical`}
                            >
                                {!!secondaryActions && (
                                    <div
                                        className={classnames({
                                            [styles.horizontalActionsContainer]: type === 'modal',
                                            [styles.verticalActionsContainer]: type === 'modal-vertical',
                                            [STRETCHED_WRAPPER_CLASS]: type === 'modal-vertical',
                                        })}
                                        ref={secondaryActionsRef}
                                        data-qa="secondary-actions"
                                    >
                                        {secondaryActions}
                                    </div>
                                )}
                                {!!additionalContent && (
                                    <div className={classnames(styles.additionalContentContainer)}>
                                        <div
                                            className={classnames(styles.additionalContentWrapper, {
                                                [styles.additionalContentOverflowWrapper]:
                                                    !!primaryActions || !!secondaryActions,
                                            })}
                                            ref={additionalContentWrapperRef}
                                        >
                                            <div
                                                ref={additionalContentRef}
                                                className={styles.additionalContentDimensions}
                                                data-qa="additional-content"
                                            >
                                                {additionalContent}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {!!primaryActions && (
                                    <div
                                        className={classnames({
                                            [styles.horizontalActionsContainer]: type === 'modal',
                                            [styles.verticalActionsContainer]: type === 'modal-vertical',
                                            [STRETCHED_WRAPPER_CLASS]: type === 'modal-vertical',
                                        })}
                                        ref={primaryActionsRef}
                                        data-qa="primary-actions"
                                    >
                                        {primaryActions}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Layer>
    );
};

ActionBar.isActionBar = true;
ActionBar.displayName = 'ActionBar';
